import React, { useState, useRef } from 'react';
import { useApiKeys } from '../contexts/ApiKeyContext';
import { useAuth } from '../contexts/AuthContext';
import OpenAI from 'openai';
import toast from 'react-hot-toast';
import {
  TrendingUp,
  Sparkles,
  ChevronRight,
  Copy,
  Download,
  Save,
  Eye,
  Zap,
  Target,
  Brain,
  Heart,
  MessageCircle,
  Lightbulb,
  Film,
  AlertCircle,
  RefreshCw,
  BookOpen,
  PenTool,
  Bot,
  Video,
  Palette,
  Layers,
  FileText,
  RotateCcw,
  Wand2,
  TrendingDown,
  Flame,
  GraduationCap,
  Mic,
  BarChart3,
  Hash,
  BookMarked,
  Shuffle,
  Settings2
} from 'lucide-react';
import {
  regenerateStoryWithChatGPT,
  regenerateStoryWithClaude,
  regenerateStoryWithGoogleAI,
  regenerateWithEmotionalTone,
  regenerateWithHookVariation,
  regenerateWithSmartOption,
  regenerateWithCombinedOptions
} from '../services/documentaryAI';

interface UnusualReason {
  number: number;
  title: string;
  description: string;
  storyAngle: string;
}

interface SpecificIdea {
  number: number;
  title: string;
  description: string;
  emotionalHook: string;
  visualElement: string;
}

interface ViralStory {
  fullStory: string;
  parts: {
    boldHook: string;
    relatableAnalogy: string;
    stepByStepReveal: string;
    contrastAndDrama: string;
    emotionalTieIn: string;
    mysteryEnding: string;
    softCTA: string;
  };
  metadata: {
    topic: string;
    wordCount: number;
    estimatedReadTime: string;
    emotionalTone: string;
    targetPlatform: string[];
  };
}

const ViralStoryGenerator: React.FC = () => {
  const { getApiKeyForModel } = useApiKeys();
  const { user, isAuthenticated } = useAuth();
  
  // State management
  const [topic, setTopic] = useState('');
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  
  // Story generation states
  const [unusualReasons, setUnusualReasons] = useState<UnusualReason[]>([]);
  const [selectedReason, setSelectedReason] = useState<number | null>(null);
  const [specificIdeas, setSpecificIdeas] = useState<SpecificIdea[]>([]);
  const [selectedIdea, setSelectedIdea] = useState<SpecificIdea | null>(null);
  const [finalStory, setFinalStory] = useState<ViralStory | null>(null);
  const [regenerateLoading, setRegenerateLoading] = useState(false);
  const [showRegenerateMenu, setShowRegenerateMenu] = useState(false);
  const [customInstructions, setCustomInstructions] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [selectedCombinedOptions, setSelectedCombinedOptions] = useState<{
    aiStyle?: 'chatgpt' | 'claude' | 'google';
    emotionalTone?: 'awe' | 'suspense' | 'inspirational' | 'educational' | 'dramatic';
    hookType?: 'question' | 'statistic' | 'story' | 'contrast';
    smartOption?: 'more_viral' | 'add_facts' | 'simplify' | 'add_controversy' | 'cinematic';
  }>({});
  
  // Optimized two-step model approach (Based on ChatGPT's recommendations)
  const brainstormModel = 'gpt-4o-mini'; // Fast/cheap for brainstorming & drafts  
  // GPT-5 has compatibility issues, using GPT-4 Turbo instead
  const storyModel = 'gpt-4-turbo-preview'; // GPT-4 Turbo for final polished scripts 
  const selectedModel = 'gpt-4-turbo-preview'; // GPT-4 Turbo for regeneration
  
  // Refs for smooth scrolling
  const step2Ref = useRef<HTMLDivElement>(null);
  const step3Ref = useRef<HTMLDivElement>(null);
  const step4Ref = useRef<HTMLDivElement>(null);
  const storyRef = useRef<HTMLDivElement>(null);
  
  const openAIKey = getApiKeyForModel('gpt4');
  const hasOpenAI = !!openAIKey;
  
  // Example viral topics from your TikTok successes
  const exampleTopics = [
    "the brain storage capacity and how memory works",
    "how deep and scary the ocean really is",
    "Deep-sea Creatures Survive Crushing Pressure",
    "time according to Albert Einstein",
    "The Amazon rain Forest ecosystem",
    "The Weight of Humanity on Earth",
    "Quantum Entanglement secrets",
    "The Lunar eclipse Bloodmoon phenomenon"
  ];
  
  // Story structure guide based on your winning format
  const storyStructureGuide = [
    {
      label: "Bold Hook",
      icon: <Zap className="w-4 h-4" />,
      color: "from-red-500 to-orange-500",
      example: "Your brain is holding the internet. No, really.",
      tips: "Curiosity + Shock. Start with an impossible-sounding fact."
    },
    {
      label: "Relatable Analogy",
      icon: <Heart className="w-4 h-4" />,
      color: "from-pink-500 to-purple-500",
      example: "That's like storing every photo and video online inside your head.",
      tips: "Make it human. Turn abstract facts into everyday comparisons."
    },
    {
      label: "Step-by-Step Reveal",
      icon: <Layers className="w-4 h-4" />,
      color: "from-blue-500 to-cyan-500",
      example: "It doesn't save files... it builds living connections.",
      tips: "Build the wonder. Short, punchy reveals."
    },
    {
      label: "Contrast & Drama",
      icon: <Zap className="w-4 h-4" />,
      color: "from-purple-500 to-indigo-500",
      example: "A warehouse-sized supercomputer guzzles megawatts. Your brain? A dim light bulb.",
      tips: "Show extremes. Highlight the miracle through opposites."
    },
    {
      label: "Emotional Tie-In",
      icon: <Heart className="w-4 h-4" />,
      color: "from-green-500 to-teal-500",
      example: "Memory isn't just data—it's your first kiss, the smell of home...",
      tips: "Make them feel it. Shift from logic to emotion."
    },
    {
      label: "Mystery Ending",
      icon: <Brain className="w-4 h-4" />,
      color: "from-yellow-500 to-orange-500",
      example: "If your brain can store the internet... what else is it capable of?",
      tips: "Open loop. Leave them curious for more."
    },
    {
      label: "Soft CTA",
      icon: <MessageCircle className="w-4 h-4" />,
      color: "from-gray-500 to-slate-600",
      example: "That's just a glimpse. The full story is waiting—link in bio.",
      tips: "Bridge to long-form. Invite without breaking mood."
    }
  ];
  
  // Step 1: Generate 10 unusual reasons
  const handleGenerateReasons = async () => {
    if (!topic.trim()) {
      toast.error('Please enter a topic first');
      return;
    }
    
    if (!hasOpenAI) {
      toast.error('Please configure your OpenAI API key in settings');
      return;
    }
    
    // Reset all previous states when generating new reasons
    setSelectedReason(null);
    setSpecificIdeas([]);
    setSelectedIdea(null);
    setFinalStory(null);
    setCurrentStep(1);
    
    setLoading(true);
    try {
      const client = new OpenAI({
        apiKey: openAIKey,
        dangerouslyAllowBrowser: true
      });
      
      const prompt = `I've been thinking about this lately: "${topic}".
      
      Can you give me 10 unusual, surprising, or underexplored reasons why this happened/existed/became important?
      
      I'm looking for story angles that most people don't talk about - things that are:
      - Counterintuitive or paradoxical
      - Emotionally powerful
      - Visually dramatic
      - Scientifically fascinating
      - Historically forgotten
      
      Return ONLY a JSON object with a "reasons" array in this exact format:
      {
        "reasons": [
          {
            "number": 1,
            "title": "Short, punchy title (5-10 words)",
            "description": "2-3 sentences explaining why this angle is fascinating",
            "storyAngle": "The unique perspective this offers"
          }
        ]
      }
      
      Make each reason progressively more surprising and unusual.`;
      
      const completion = await client.chat.completions.create({
        model: brainstormModel, // GPT-4o-mini for fast brainstorming
        messages: [
          { 
            role: "system", 
            content: "You are a viral content expert who finds the most unusual and fascinating angles in any topic. Think like a documentary filmmaker looking for the untold story. You MUST respond with a valid JSON object containing a 'reasons' array. No other text or formatting."
          },
          { role: "user", content: prompt }
        ],
        ...(brainstormModel === 'gpt-5' ? {} : { temperature: 0.8 }), // GPT-5 only supports default temperature (1)
        ...(brainstormModel === 'gpt-5' ? { max_completion_tokens: 2500 } : { max_tokens: 2500 }),
        ...(brainstormModel === 'gpt-4o-mini' ? { response_format: { type: "json_object" } } : {}) // Only for models that support it
      });
      
      // Parse response
      const rawContent = completion.choices[0].message.content || '{"reasons":[]}';
      console.log('Raw response from GPT-4o-mini:', rawContent);
      
      let response;
      try {
        response = JSON.parse(rawContent);
      } catch (parseError) {
        console.error('JSON parse error:', parseError);
        console.log('Raw content that failed to parse:', rawContent);
        throw new Error('Failed to parse AI response');
      }
      
      // Handle different response formats
      let reasons = response.reasons || response.angles || response;
      
      // If response is an object with a data field
      if (!Array.isArray(reasons) && reasons.data) {
        reasons = reasons.data;
      }
      
      // Ensure we have an array
      if (!Array.isArray(reasons)) {
        if (typeof reasons === 'object' && reasons !== null) {
          // Try to extract array from object
          reasons = Object.values(reasons);
        }
      }
      
      if (Array.isArray(reasons) && reasons.length > 0) {
        setUnusualReasons(reasons);
        setCurrentStep(2);
        setTimeout(() => {
          step2Ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
        toast.success(`🎯 Found ${reasons.length} unusual angles!`);
      } else {
        console.error('No valid reasons found in response:', response);
        throw new Error('No reasons generated');
      }
    } catch (error: any) {
      console.error('Error generating reasons:', error);
      
      // More specific error messages
      if (error.message?.includes('401') || error.message?.includes('Incorrect API key')) {
        toast.error('Invalid API key. Please check your OpenAI key in settings.');
      } else if (error.message?.includes('429')) {
        toast.error('Rate limit exceeded. Please wait a moment and try again.');
      } else if (error.message?.includes('insufficient_quota')) {
        toast.error('OpenAI quota exceeded. Please check your OpenAI account.');
      } else {
        toast.error(`Error: ${error.message || 'Failed to generate story angles'}`);
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Step 2: Expand selected reason into 5 specific ideas
  const handleExpandReason = async (reasonNumber: number) => {
    if (!hasOpenAI) return;
    
    // Reset previous selections when choosing a new reason
    setSpecificIdeas([]);
    setSelectedIdea(null);
    setFinalStory(null);
    
    const selectedReasonData = unusualReasons[reasonNumber - 1];
    setSelectedReason(reasonNumber);
    setLoading(true);
    
    try {
      const client = new OpenAI({
        apiKey: openAIKey,
        dangerouslyAllowBrowser: true
      });
      
      const prompt = `I think reason number ${reasonNumber} is really interesting:
      "${selectedReasonData.title}: ${selectedReasonData.description}"
      
      Can you break down that one reason into 5 short, specific ideas — events, turning points, contradictions, or facts — that are:
      - Shocking or unexpected
      - Emotionally powerful
      - Visually dramatic
      - Tightly related to that reason
      
      Each idea should be a specific moment, fact, or revelation that would make someone stop scrolling.
      
      Return EXACTLY 5 ideas as a JSON object with this structure:
      {
        "ideas": [
          {
            "number": 1,
            "title": "Specific, concrete title",
            "description": "2-3 sentences with specific details, numbers, or examples",
            "emotionalHook": "The feeling this evokes (wonder, shock, fear, awe, etc.)",
            "visualElement": "What viewers would see in their mind"
          }
        ]
      }
      
      Make sure to return exactly 5 ideas in the array.`;
      
      const completion = await client.chat.completions.create({
        model: brainstormModel, // GPT-4o-mini for fast variations
        messages: [
          { 
            role: "system", 
            content: "You are a master storyteller who finds specific, concrete moments that illustrate abstract ideas. Think cinematically - what would make a powerful scene? You MUST respond with a valid JSON object containing an 'ideas' array. No other text or formatting."
          },
          { role: "user", content: prompt }
        ],
        ...(brainstormModel === 'gpt-5' ? {} : { temperature: 0.8 }), // GPT-5 uses default
        ...(brainstormModel === 'gpt-5' ? { max_completion_tokens: 2000 } : { max_tokens: 2000 }),
        ...(brainstormModel === 'gpt-4o-mini' ? { response_format: { type: "json_object" } } : {}) // Only for models that support it
      });
      
      // Parse response
      const response = JSON.parse(completion.choices[0].message.content || '{"ideas":[]}');
      const ideas = response.ideas || response.specificIdeas || response;
      
      // Ensure we have an array of ideas
      const ideaArray = Array.isArray(ideas) ? ideas : (ideas.ideas || []);
      
      if (ideaArray.length > 0) {
        setSpecificIdeas(ideaArray);
        setCurrentStep(3);
        setTimeout(() => {
          step3Ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
        toast.success(`💡 Generated ${ideaArray.length} specific story ideas!`);
      } else {
        throw new Error('No specific ideas generated');
      }
    } catch (error: any) {
      console.error('Error expanding reason:', error);
      
      // More detailed error message
      if (error.message?.includes('401')) {
        toast.error('Invalid API key. Please check your OpenAI key.');
      } else if (error.message?.includes('429')) {
        toast.error('Rate limit exceeded. Please wait and try again.');
      } else {
        toast.error(`Error: ${error.message || 'Failed to expand story idea'}`);
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Step 3: Create final viral story
  const handleCreateStory = async (idea: SpecificIdea) => {
    if (!hasOpenAI) return;
    
    setSelectedIdea(idea);
    setLoading(true);
    
    try {
      const client = new OpenAI({
        apiKey: openAIKey,
        dangerouslyAllowBrowser: true
      });
      
      const prompt = `Create a viral TikTok documentary story about this SPECIFIC phenomenon:
      
      TITLE: "${idea.title}"
      DESCRIPTION: ${idea.description}
      EMOTION: ${idea.emotionalHook}
      VISUAL: ${idea.visualElement}
      
      CRITICAL INSTRUCTIONS:
      - Write ONLY about "${idea.title}" - Be specific, not general
      - Use the exact description: "${idea.description}"
      - Total length: 220-260 words (MANDATORY for TikTok timing)
      - Style: Like "20 meters below the surface. And that's only 2% of the ocean's depth."
      - Short sentences. Line breaks. Cinematic flow.
      
      Follow this EXACT 7-part structure:
      
      1. Bold Hook (25-35 words)
      Open with the most shocking aspect of "${idea.title}".
      For example, if it's about "Creative Connections" in dreams, start with: "Every night, your brain creates impossible connections. A paper plane made from childhood drawings takes flight in your dreams."
      
      2. Relatable Analogy (40-50 words)
      Explain "${idea.title}" using everyday comparisons.
      For "Creative Connections": "It's like your brain becomes a mad scientist, mixing memories like chemicals. Your first day of school merges with yesterday's coffee. Your grandmother's voice narrates a movie you've never seen."
      
      3. Step-by-Step Reveal (60-80 words)
      Explain exactly HOW "${idea.title}" works with facts and numbers.
      Use the description: "${idea.description}"
      
      Example for "The Forgotten Lullaby":
      "Music activates the hippocampus. Even in advanced dementia. 60% of Alzheimer's patients respond to familiar songs. Neural pathways destroyed by disease suddenly reconnect. The auditory cortex links to deep memory storage. Areas untouched by cognitive decline. A mother's lullaby from 70 years ago. Still intact. Still powerful. The patient's brain finds a path through the ruins."
      
      4. Contrast & Drama (45-55 words)
      Show the incredible contrast in "${idea.title}".
      "While awake, your brain follows rules. Neural highways. Predictable routes. But in dreams? It's neural anarchy. Beautiful chaos. A childhood drawing becomes a vehicle. A forgotten face becomes your guide. Impossible becomes inevitable."
      
      5. Emotional Tie-In (35-45 words)
      Connect to the emotion: ${idea.emotionalHook}
      "These bizarre connections aren't random. They're your brain solving problems you didn't know existed. Processing emotions too complex for consciousness. Every strange dream is your mind healing, learning, creating."
      
      6. Mystery Ending (30-40 words)
      End with a question about "${idea.title}".
      "If our sleeping brains can merge a paper plane with childhood memories and make it fly... what other impossible connections are we making that we'll never remember?"
      
      7. Soft CTA (10-15 words)
      "Perhaps our strangest dreams hold our greatest insights..."
      
      Return as JSON with this structure:
      {
        "fullStory": "Complete story with all 7 parts flowing naturally",
        "parts": {
          "boldHook": "The hook text",
          "relatableAnalogy": "The analogy text",
          "stepByStepReveal": "The reveal text",
          "contrastAndDrama": "The contrast text",
          "emotionalTieIn": "The emotional text",
          "mysteryEnding": "The mystery text",
          "softCTA": "The CTA text"
        },
        "metadata": {
          "topic": "${topic}",
          "wordCount": number,
          "estimatedReadTime": "X seconds",
          "emotionalTone": "primary emotion",
          "targetPlatform": ["TikTok", "Instagram", "YouTube Shorts"]
        }
      }`;
      
      const completion = await client.chat.completions.create({
        model: storyModel, // GPT-4o for polished final story
        messages: [
          { 
            role: "system", 
            content: `You are creating viral TikTok documentaries that get millions of views.
            
            ABSOLUTE REQUIREMENTS:
            
            1. Focus ONLY on the SPECIFIC IDEA provided (e.g., "Creative Connections" not general dreams)
            2. Use the EXACT description given to shape your story
            3. Total length: 220-260 words (MANDATORY - this is optimal TikTok length)
            
            WORD DISTRIBUTION (Total: 220-260):
            - Bold Hook: 25-35 words
            - Relatable Analogy: 40-50 words
            - Step-by-Step Reveal: 60-80 words (biggest section)
            - Contrast & Drama: 45-55 words
            - Emotional Tie-In: 35-45 words
            - Mystery Ending: 30-40 words
            - Soft CTA: 10-15 words
            
            WRITING STYLE - MUST follow these viral documentary patterns:
            - "20 meters below the surface. And that's only 2% of the ocean's depth."
            - "At 1000 meters, the midnight zone. No light ever reaches."
            - "Your brain uses 20 watts. Less than a dim light bulb."
            - "86 billion neurons. Thousands of connections each."
            
            MANDATORY elements:
            - Include specific numbers (percentages, measurements, timeframes)
            - Short sentences. Period after each fact.
            - Progressive revelation (shallow → deep)
            - Scientific facts mixed with human emotion
            - Concrete visual details
            - No flowery language - be direct and impactful
            
            The fullStory must flow naturally while each part stands alone.
            Always respond with valid JSON.`
          },
          { role: "user", content: prompt }
        ],
        temperature: 0.8, // ChatGPT recommended
        max_tokens: 3000, // ChatGPT: "2000-3000 for full output"
        response_format: { type: "json_object" }
      });
      
      // Parse response
      const rawContent = completion.choices[0].message.content || '{}';
      console.log('Raw story response from model:', rawContent);
      console.log('Model used:', storyModel);
      
      let story;
      try {
        // Try to parse as JSON
        story = JSON.parse(rawContent);
      } catch (e) {
        // If not JSON, try to extract JSON from the text
        console.log('Response is not pure JSON, attempting to extract...');
        const jsonMatch = rawContent.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          story = JSON.parse(jsonMatch[0]);
        } else {
          throw new Error('Could not extract valid JSON from response');
        }
      }
      
      // Check for story in different possible formats
      const finalStoryContent = story.fullStory || story.story || story.content || story.full_story;
      
      if (finalStoryContent) {
        // Ensure we have the right structure
        if (!story.fullStory) {
          story.fullStory = finalStoryContent;
        }
        // Ensure parts exist even if empty
        if (!story.parts) {
          story.parts = {
            boldHook: '',
            relatableAnalogy: '',
            stepByStepReveal: '',
            contrastAndDrama: '',
            emotionalTieIn: '',
            mysteryEnding: '',
            softCTA: ''
          };
        }
        setFinalStory(story);
        setCurrentStep(4);
        setTimeout(() => {
          storyRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
        toast.success('🎬 Viral story created successfully!');
      } else {
        console.error('Story structure incomplete:', story);
        console.error('Expected fields not found. Response structure:', Object.keys(story));
        toast.error('Story generation incomplete. Please try again.');
      }
    } catch (error: any) {
      console.error('Error creating story:', error);
      
      // More detailed error messages
      if (error.message?.includes('model') || error.message?.includes('gpt-5')) {
        console.error('Model error - GPT-5 might not be available in API yet');
        toast.error('Note: GPT-5 API access pending. Using GPT-4 Turbo.');
      } else if (error.message?.includes('401')) {
        toast.error('Invalid API key. Please check your OpenAI key.');
      } else if (error.message?.includes('429')) {
        toast.error('Rate limit exceeded. Please wait and try again.');
      } else {
        toast.error(`Failed to create story: ${error.message || 'Unknown error'}`);
      }
    } finally {
      setLoading(false);
    }
  };
  
  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    toast.success('📋 Copied to clipboard!');
    setTimeout(() => setCopied(null), 2000);
  };
  
  // Reset everything to start over
  const handleStartOver = () => {
    setTopic('');
    setCurrentStep(1);
    setUnusualReasons([]);
    setSelectedReason(null);
    setSpecificIdeas([]);
    setSelectedIdea(null);
    setFinalStory(null);
    setCopied(null);
    toast.success('Ready to create a new story!');
  };
  
  // Handle combined regeneration
  const handleCombinedRegenerate = async () => {
    if (!selectedIdea || !selectedReason || !openAIKey) return;
    
    // Check if any options are selected
    if (Object.keys(selectedCombinedOptions).length === 0) {
      toast.error('Please select at least one regeneration option');
      return;
    }
    
    setRegenerateLoading(true);
    
    try {
      // Get the selected reason from unusualReasons array
      const currentReason = unusualReasons.find(r => r.number === selectedReason);
      if (!currentReason) {
        throw new Error('Selected reason not found');
      }
      
      // Create ExpandedReason object for the API functions
      const expandedReason = {
        number: selectedIdea.number,
        title: selectedIdea.title,
        description: selectedIdea.description,
        emotionalHook: selectedIdea.emotionalHook,
        visualElement: selectedIdea.visualElement
      };
      
      const newStory = await regenerateWithCombinedOptions(
        topic,
        expandedReason,
        openAIKey,
        selectedCombinedOptions,
        selectedModel,
        finalStory?.fullStory
      );
      
      setFinalStory(newStory);
      toast.success('Story regenerated with combined options!');
    } catch (error) {
      console.error('Error regenerating story:', error);
      toast.error('Failed to regenerate story. Please try again.');
    } finally {
      setRegenerateLoading(false);
    }
  };

  // Regenerate story handlers
  const handleRegenerateStory = async (type: string, value?: string) => {
    if (!selectedIdea || !selectedReason || !openAIKey) return;
    
    setRegenerateLoading(true);
    setShowRegenerateMenu(false);
    setShowCustomInput(false);
    
    try {
      let newStory: ViralStory;
      
      // Get the selected reason from unusualReasons array
      const currentReason = unusualReasons.find(r => r.number === selectedReason);
      if (!currentReason) {
        throw new Error('Selected reason not found');
      }
      
      // Create ExpandedReason object for the API functions
      const expandedReason = {
        number: selectedIdea.number,
        title: selectedIdea.title,
        description: selectedIdea.description,
        emotionalHook: selectedIdea.emotionalHook,
        visualElement: selectedIdea.visualElement
      };
      
      // Pass the current story to maintain structure
      const currentStory = finalStory;
      
      switch (type) {
        // AI Styles - maintain story structure, only change voice
        case 'chatgpt':
          newStory = await regenerateStoryWithChatGPT(topic, expandedReason, openAIKey, currentStory?.fullStory, selectedModel);
          break;
        case 'claude':
          newStory = await regenerateStoryWithClaude(topic, expandedReason, openAIKey, currentStory?.fullStory, selectedModel);
          break;
        case 'google':
          newStory = await regenerateStoryWithGoogleAI(topic, expandedReason, openAIKey, currentStory?.fullStory, selectedModel);
          break;
        
        // Emotional Tones
        case 'awe':
        case 'suspense':
        case 'inspirational':
        case 'educational':
        case 'dramatic':
          // Pass current story to maintain structure
          newStory = await regenerateWithEmotionalTone(topic, expandedReason, openAIKey, type as any, selectedModel, currentStory?.fullStory);
          break;
        
        // Hook Variations
        case 'question_hook':
          newStory = await regenerateWithHookVariation(topic, expandedReason, openAIKey, 'question', undefined, selectedModel, currentStory?.fullStory);
          break;
        case 'statistic_hook':
          newStory = await regenerateWithHookVariation(topic, expandedReason, openAIKey, 'statistic', undefined, selectedModel, currentStory?.fullStory);
          break;
        case 'story_hook':
          newStory = await regenerateWithHookVariation(topic, expandedReason, openAIKey, 'story', undefined, selectedModel, currentStory?.fullStory);
          break;
        case 'contrast_hook':
          newStory = await regenerateWithHookVariation(topic, expandedReason, openAIKey, 'contrast', undefined, selectedModel, currentStory?.fullStory);
          break;
        case 'custom_hook':
          newStory = await regenerateWithHookVariation(topic, expandedReason, openAIKey, 'custom', value, selectedModel, currentStory?.fullStory);
          break;
        
        // Smart Options
        case 'more_viral':
        case 'add_facts':
        case 'simplify':
        case 'add_controversy':
        case 'cinematic':
          newStory = await regenerateWithSmartOption(topic, expandedReason, openAIKey, type as any, undefined, selectedModel, currentStory?.fullStory);
          break;
        case 'custom_smart':
          newStory = await regenerateWithSmartOption(topic, expandedReason, openAIKey, 'custom', value, selectedModel, currentStory?.fullStory);
          break;
        
        default:
          throw new Error('Unknown regeneration type');
      }
      
      setFinalStory(newStory);
      toast.success('Story regenerated successfully!');
    } catch (error) {
      console.error('Error regenerating story:', error);
      toast.error('Failed to regenerate story. Please try again.');
    } finally {
      setRegenerateLoading(false);
      setCustomInstructions('');
    }
  };

  // Helper function to calculate readability score (Flesch Reading Ease)
  const calculateReadabilityScore = (text: string): number => {
    // Remove extra spaces and split into words
    const words = text.trim().split(/\s+/).filter(word => word.length > 0);
    const wordCount = words.length;
    
    // Count sentences (. ! ? as sentence endings)
    const sentences = text.split(/[.!?]+/).filter(s => s.trim().length > 0);
    const sentenceCount = sentences.length || 1;
    
    // Count syllables (simplified - counts vowel groups)
    const countSyllables = (word: string): number => {
      word = word.toLowerCase();
      let count = 0;
      let previousWasVowel = false;
      
      for (let i = 0; i < word.length; i++) {
        const isVowel = /[aeiou]/.test(word[i]);
        if (isVowel && !previousWasVowel) {
          count++;
        }
        previousWasVowel = isVowel;
      }
      
      // Ensure at least 1 syllable per word
      return Math.max(1, count);
    };
    
    const totalSyllables = words.reduce((sum, word) => sum + countSyllables(word), 0);
    
    // Flesch Reading Ease formula
    const score = 206.835 - 1.015 * (wordCount / sentenceCount) - 84.6 * (totalSyllables / wordCount);
    
    // Clamp between 0 and 100
    return Math.max(0, Math.min(100, Math.round(score)));
  };
  
  // Helper function to get the actual combined story text
  const getCombinedStoryText = (story: ViralStory): string => {
    if (!story || !story.parts) return '';
    
    return [
      story.parts.boldHook || '',
      story.parts.relatableAnalogy || '',
      story.parts.stepByStepReveal || '',
      story.parts.contrastAndDrama || '',
      story.parts.emotionalTieIn || '',
      story.parts.mysteryEnding || '',
      story.parts.softCTA || ''
    ].filter(part => part).join(' ');
  };
  
  // Helper function to calculate actual word count
  const getActualWordCount = (story: ViralStory): number => {
    const fullText = getCombinedStoryText(story);
    return fullText.trim().split(/\s+/).filter(word => word.length > 0).length;
  };
  
  // Helper function to calculate read time
  const getReadTime = (wordCount: number): string => {
    // Average reading speed is 150-200 words per minute for social media
    // Using 150 for TikTok/shorts content
    const wordsPerMinute = 150;
    const minutes = wordCount / wordsPerMinute;
    const seconds = Math.ceil(minutes * 60);
    return `${seconds} seconds`;
  };
  
  // Helper function to detect emotional tone from the story
  const detectEmotionalTone = (story: ViralStory): string => {
    if (!story || !story.parts) return 'Neutral';
    
    const fullText = getCombinedStoryText(story).toLowerCase();
    
    // Keywords for different tones
    const toneKeywords = {
      'Awe & Wonder': ['amazing', 'incredible', 'wonder', 'miracle', 'astonish', 'remarkable', 'breathtaking', 'magnificent'],
      'Suspenseful': ['mystery', 'unknown', 'hidden', 'secret', 'dark', 'lurking', 'shadow', 'reveal'],
      'Inspirational': ['hope', 'dream', 'achieve', 'inspire', 'courage', 'strength', 'overcome', 'triumph'],
      'Educational': ['learn', 'discover', 'understand', 'science', 'research', 'study', 'fact', 'data'],
      'Dramatic': ['shocking', 'intense', 'extreme', 'powerful', 'clash', 'conflict', 'struggle', 'battle'],
      'Philosophical': ['meaning', 'existence', 'consciousness', 'reality', 'truth', 'wisdom', 'soul', 'essence'],
      'Conversational': ['you', 'we', 'our', 'let\'s', 'imagine', 'think about', 'consider', 'ever wondered']
    };
    
    // Count matches for each tone
    let maxMatches = 0;
    let detectedTone = 'Neutral';
    
    for (const [tone, keywords] of Object.entries(toneKeywords)) {
      const matches = keywords.filter(keyword => fullText.includes(keyword)).length;
      if (matches > maxMatches) {
        maxMatches = matches;
        detectedTone = tone;
      }
    }
    
    // Check metadata for AI-specified tone
    if (story.metadata?.emotionalTone && story.metadata.emotionalTone !== 'default') {
      const toneMap: Record<string, string> = {
        'awe': 'Awe & Wonder',
        'suspense': 'Suspenseful',
        'inspirational': 'Inspirational',
        'educational': 'Educational',
        'dramatic': 'Dramatic'
      };
      return toneMap[story.metadata.emotionalTone] || detectedTone;
    }
    
    return detectedTone;
  };

  const downloadStory = () => {
    if (!finalStory) return;
    
    // Always use combined parts for consistency
    const fullStoryText = `${finalStory.parts.boldHook} ${finalStory.parts.relatableAnalogy} ${finalStory.parts.stepByStepReveal} ${finalStory.parts.contrastAndDrama} ${finalStory.parts.emotionalTieIn} ${finalStory.parts.mysteryEnding} ${finalStory.parts.softCTA}`;
    
    const content = `VIRAL STORY: ${finalStory.metadata?.topic || 'Generated Story'}
    
${fullStoryText}

---
STORY BREAKDOWN:

1. BOLD HOOK:
${finalStory.parts.boldHook}

2. RELATABLE ANALOGY:
${finalStory.parts.relatableAnalogy}

3. STEP-BY-STEP REVEAL:
${finalStory.parts.stepByStepReveal}

4. CONTRAST & DRAMA:
${finalStory.parts.contrastAndDrama}

5. EMOTIONAL TIE-IN:
${finalStory.parts.emotionalTieIn}

6. MYSTERY ENDING:
${finalStory.parts.mysteryEnding}

7. SOFT CTA:
${finalStory.parts.softCTA}

---
METADATA:
- Word Count: ${getActualWordCount(finalStory)} words
- Read Time: ${getReadTime(getActualWordCount(finalStory))}
- Emotional Tone: ${detectEmotionalTone(finalStory)}
- Readability Score: ${(() => {
    const score = calculateReadabilityScore(fullStoryText);
    let level = 'Complex';
    if (score >= 90) level = 'Very Easy';
    else if (score >= 80) level = 'Easy';
    else if (score >= 70) level = 'Fairly Easy';
    else if (score >= 60) level = 'Standard';
    else if (score >= 50) level = 'Fairly Difficult';
    else if (score >= 30) level = 'Difficult';
    return `${score}/100 (${level})`;
  })()}
`;
    
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `viral-story-${Date.now()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  
  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 via-pink-600 to-red-600 text-white rounded-xl p-8 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <TrendingUp className="w-12 h-12" />
            <div>
              <h1 className="text-4xl font-bold">Viral Story Generator</h1>
              <p className="text-xl opacity-90 mt-2">
                Create 1M+ views stories using the proven 7-part viral structure
              </p>
            </div>
          </div>
          {(unusualReasons.length > 0 || finalStory) && (
            <button
              onClick={handleStartOver}
              className="px-4 py-2 bg-white/20 hover:bg-white/30 backdrop-blur rounded-lg flex items-center gap-2 transition-colors"
            >
              <RotateCcw className="w-5 h-5" />
              Start Over
            </button>
          )}
        </div>
        
        <div className="mt-6 grid grid-cols-4 gap-4">
          <div className="bg-white/10 backdrop-blur rounded-lg p-3">
            <Eye className="w-6 h-6 mb-2" />
            <p className="text-sm font-semibold">Hook in 3 Seconds</p>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-lg p-3">
            <Heart className="w-6 h-6 mb-2" />
            <p className="text-sm font-semibold">Emotional Connection</p>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-lg p-3">
            <Brain className="w-6 h-6 mb-2" />
            <p className="text-sm font-semibold">Curiosity Loops</p>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-lg p-3">
            <Film className="w-6 h-6 mb-2" />
            <p className="text-sm font-semibold">Cinematic Style</p>
          </div>
        </div>
      </div>
      
      {/* API Key Check */}
      {!hasOpenAI && (
        <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
            <div>
              <p className="font-semibold text-yellow-900">OpenAI API Key Required</p>
              <p className="text-sm text-yellow-700 mt-1">
                Please configure your OpenAI API key in settings to use the viral story generator.
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Progress Indicator */}
      {currentStep > 1 && (
        <div className="bg-white rounded-xl shadow-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className={`px-3 py-1 rounded-full text-sm font-medium ${currentStep >= 1 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}>
                ✓ Topic Selected
              </div>
              {currentStep >= 2 && (
                <div className={`px-3 py-1 rounded-full text-sm font-medium ${currentStep >= 2 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}>
                  ✓ {unusualReasons.length} Angles Found
                </div>
              )}
              {currentStep >= 3 && selectedReason && (
                <div className={`px-3 py-1 rounded-full text-sm font-medium ${currentStep >= 3 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}>
                  ✓ Angle #{selectedReason} Selected
                </div>
              )}
              {currentStep >= 4 && (
                <div className="px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-700">
                  ✓ Story Created
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Step 1: Topic Input */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full flex items-center justify-center text-white font-bold">
            1
          </div>
          <h2 className="text-2xl font-bold text-gray-900">
            🟨 Enter Your Topic
          </h2>
        </div>
        
        {/* Optimized Model Strategy Info */}
        <div className="mb-4 bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Settings2 className="w-5 h-5 text-blue-600 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold text-gray-900 mb-2">🚀 Optimized AI Model Strategy with GPT-5</p>
              <div className="space-y-1 text-sm">
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded font-medium">Step 1-2</span>
                  <span className="text-gray-700"><strong>GPT-4o mini</strong> → Fast brainstorming (10 angles + 5 variations)</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded font-medium">Step 3</span>
                  <span className="text-gray-700"><strong>GPT-4 Turbo</strong> → Final polished story (most advanced available)</span>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2 italic">Temperature: 0.8 | Max tokens: 2000-3000 | Based on ChatGPT's viral content recommendations</p>
            </div>
          </div>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              What topic, event, or idea are you curious about?
            </label>
            <textarea
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="Example: the brain storage capacity and how memory works..."
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
              rows={3}
            />
          </div>
          
          <div>
            <p className="text-sm text-gray-600 mb-2">Your winning topics:</p>
            <div className="flex flex-wrap gap-2">
              {exampleTopics.map((example, idx) => (
                <button
                  key={idx}
                  onClick={() => setTopic(example)}
                  className="px-3 py-1.5 bg-gradient-to-r from-purple-100 to-pink-100 hover:from-purple-200 hover:to-pink-200 text-sm rounded-full transition-all"
                >
                  {example}
                </button>
              ))}
            </div>
          </div>
          
          <button
            onClick={handleGenerateReasons}
            disabled={!topic.trim() || !hasOpenAI || loading}
            className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading && currentStep === 1 ? (
              <>
                <RefreshCw className="w-5 h-5 animate-spin" />
                Finding unusual angles...
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5" />
                Generate 10 Unusual Reasons
              </>
            )}
          </button>
        </div>
      </div>
      
      {/* Step 2: Select Unusual Reason */}
      {unusualReasons.length > 0 && (
        <div ref={step2Ref} className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-gradient-to-r from-pink-600 to-red-600 rounded-full flex items-center justify-center text-white font-bold">
              2
            </div>
            <h2 className="text-2xl font-bold text-gray-900">
              🟨 Choose Your Story Angle
            </h2>
            <span className="text-sm text-gray-500">(Click one to expand)</span>
          </div>
          
          <div className="grid gap-3">
            {unusualReasons.map((reason) => (
              <button
                key={reason.number}
                onClick={() => handleExpandReason(reason.number)}
                disabled={loading}
                className={`text-left p-4 rounded-lg border-2 transition-all ${
                  selectedReason === reason.number
                    ? 'border-purple-500 bg-purple-50'
                    : 'border-gray-200 hover:border-purple-300 hover:bg-purple-50/50'
                } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                    {reason.number}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 mb-1">{reason.title}</h3>
                    <p className="text-sm text-gray-600 mb-2">{reason.description}</p>
                    <p className="text-xs text-purple-600 italic">
                      Angle: {reason.storyAngle}
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
      
      {/* Step 3: Select Specific Idea */}
      {(selectedReason && (specificIdeas.length > 0 || (loading && currentStep === 2))) && (
        <div ref={step3Ref} className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-gradient-to-r from-red-600 to-orange-600 rounded-full flex items-center justify-center text-white font-bold">
              3
            </div>
            <h2 className="text-2xl font-bold text-gray-900">
              🟨 Pick Your Specific Story
            </h2>
            {specificIdeas.length > 0 && (
              <span className="text-sm text-gray-500">(Click to generate full story)</span>
            )}
          </div>
          
          {loading && currentStep === 2 ? (
            <div className="flex items-center justify-center py-12">
              <div className="flex flex-col items-center gap-3">
                <RefreshCw className="w-8 h-8 animate-spin text-purple-600" />
                <p className="text-gray-600">Generating specific story ideas...</p>
              </div>
            </div>
          ) : (
            <div className="grid gap-3">
              {specificIdeas.map((idea) => (
                <button
                  key={idea.number}
                  onClick={() => handleCreateStory(idea)}
                  disabled={loading}
                  className={`text-left p-4 rounded-lg border-2 transition-all ${
                    selectedIdea?.number === idea.number
                      ? 'border-red-500 bg-red-50'
                      : 'border-gray-200 hover:border-red-300 hover:bg-red-50/50'
                  } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className="flex items-start gap-3">
                    <Lightbulb className="w-6 h-6 text-orange-500 flex-shrink-0" />
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 mb-1">{idea.title}</h3>
                      <p className="text-sm text-gray-600 mb-2">{idea.description}</p>
                      <div className="flex items-center gap-4 text-xs">
                        <span className="flex items-center gap-1">
                          <Heart className="w-3 h-3 text-red-500" />
                          <span className="text-gray-600">{idea.emotionalHook}</span>
                        </span>
                        <span className="flex items-center gap-1">
                          <Eye className="w-3 h-3 text-blue-500" />
                          <span className="text-gray-600">{idea.visualElement}</span>
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      
      {/* Final Story Display */}
      {finalStory && (
        <div ref={storyRef} className="space-y-6">
          {/* Story Actions */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Film className="w-6 h-6" />
                Your Viral Story
              </h2>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    // Always combine all parts for consistency
                    const completeStory = `${finalStory.parts.boldHook} ${finalStory.parts.relatableAnalogy} ${finalStory.parts.stepByStepReveal} ${finalStory.parts.contrastAndDrama} ${finalStory.parts.emotionalTieIn} ${finalStory.parts.mysteryEnding} ${finalStory.parts.softCTA}`;
                    copyToClipboard(completeStory, 'full-story');
                  }}
                  className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                    copied === 'full-story'
                      ? 'bg-green-600 text-white'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  <Copy className="w-4 h-4" />
                  {copied === 'full-story' ? 'Copied!' : 'Copy Story'}
                </button>
                <button
                  onClick={downloadStory}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Download
                </button>
              </div>
            </div>
            
            {/* Full Story Display */}
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-6 border-2 border-purple-200">
              <div className="prose prose-lg max-w-none">
                <div className="whitespace-pre-wrap font-serif text-gray-800 leading-relaxed">
                  {/* Always display the combined parts for consistency */}
                  {finalStory.parts.boldHook && (
                    <>{finalStory.parts.boldHook} </>
                  )}
                  {finalStory.parts.relatableAnalogy && (
                    <>{finalStory.parts.relatableAnalogy} </>
                  )}
                  {finalStory.parts.stepByStepReveal && (
                    <>{finalStory.parts.stepByStepReveal} </>
                  )}
                  {finalStory.parts.contrastAndDrama && (
                    <>{finalStory.parts.contrastAndDrama} </>
                  )}
                  {finalStory.parts.emotionalTieIn && (
                    <>{finalStory.parts.emotionalTieIn} </>
                  )}
                  {finalStory.parts.mysteryEnding && (
                    <>{finalStory.parts.mysteryEnding} </>
                  )}
                  {finalStory.parts.softCTA && (
                    <>{finalStory.parts.softCTA}</>
                  )}
                </div>
              </div>
            </div>
            
            {/* Story Metadata */}
            {finalStory && (
              <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Word Count</p>
                  <p className="text-sm font-semibold text-gray-900">{getActualWordCount(finalStory)} words</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Read Time</p>
                  <p className="text-sm font-semibold text-gray-900">{getReadTime(getActualWordCount(finalStory))}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Emotional Tone</p>
                  <p className="text-sm font-semibold text-gray-900">{detectEmotionalTone(finalStory)}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Readability Score</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {(() => {
                      const score = calculateReadabilityScore(getCombinedStoryText(finalStory));
                      let level = 'Complex';
                      if (score >= 90) level = 'Very Easy';
                      else if (score >= 80) level = 'Easy';
                      else if (score >= 70) level = 'Fairly Easy';
                      else if (score >= 60) level = 'Standard';
                      else if (score >= 50) level = 'Fairly Difficult';
                      else if (score >= 30) level = 'Difficult';
                      return `${score}/100 (${level})`;
                    })()}
                  </p>
                </div>
              </div>
            )}
            
            {/* Regenerate Story Options */}
            <div className="mt-6 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-4 border border-indigo-200">
              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <Wand2 className="w-4 h-4" />
                    Regenerate Your Story
                  </h4>
                  {regenerateLoading && (
                    <div className="flex items-center gap-2 text-sm text-indigo-600">
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Regenerating...
                    </div>
                  )}
                </div>
                
                {!showRegenerateMenu ? (
                  <button
                    onClick={() => setShowRegenerateMenu(true)}
                    disabled={regenerateLoading}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 justify-center"
                  >
                    <Sparkles className="w-4 h-4" />
                    Show Regeneration Options
                  </button>
                ) : (
                  <>
                  {/* Quick Regenerate Buttons */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                    {/* AI Styles */}
                    <div className="space-y-2">
                      <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider">AI Styles</p>
                      <button
                        onClick={() => handleRegenerateStory('chatgpt')}
                        disabled={regenerateLoading}
                        className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-sm text-left flex items-center gap-2 disabled:opacity-50"
                      >
                        <Bot className="w-4 h-4 text-green-500" />
                        ChatGPT (Conversational)
                      </button>
                      <button
                        onClick={() => handleRegenerateStory('claude')}
                        disabled={regenerateLoading}
                        className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-sm text-left flex items-center gap-2 disabled:opacity-50"
                      >
                        <Brain className="w-4 h-4 text-purple-500" />
                        Claude (Philosophical)
                      </button>
                      <button
                        onClick={() => handleRegenerateStory('google')}
                        disabled={regenerateLoading}
                        className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-sm text-left flex items-center gap-2 disabled:opacity-50"
                      >
                        <BarChart3 className="w-4 h-4 text-blue-500" />
                        Google AI (Scientific)
                      </button>
                    </div>
                    
                    {/* Emotional Tones */}
                    <div className="space-y-2">
                      <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Emotional Tones</p>
                      <button
                        onClick={() => handleRegenerateStory('awe')}
                        disabled={regenerateLoading}
                        className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-sm text-left flex items-center gap-2 disabled:opacity-50"
                      >
                        <Sparkles className="w-4 h-4 text-yellow-500" />
                        Awe & Wonder
                      </button>
                      <button
                        onClick={() => handleRegenerateStory('suspense')}
                        disabled={regenerateLoading}
                        className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-sm text-left flex items-center gap-2 disabled:opacity-50"
                      >
                        <Eye className="w-4 h-4 text-red-500" />
                        Suspenseful
                      </button>
                      <button
                        onClick={() => handleRegenerateStory('inspirational')}
                        disabled={regenerateLoading}
                        className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-sm text-left flex items-center gap-2 disabled:opacity-50"
                      >
                        <Heart className="w-4 h-4 text-pink-500" />
                        Inspirational
                      </button>
                      <button
                        onClick={() => handleRegenerateStory('educational')}
                        disabled={regenerateLoading}
                        className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-sm text-left flex items-center gap-2 disabled:opacity-50"
                      >
                        <GraduationCap className="w-4 h-4 text-indigo-500" />
                        Educational
                      </button>
                      <button
                        onClick={() => handleRegenerateStory('dramatic')}
                        disabled={regenerateLoading}
                        className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-sm text-left flex items-center gap-2 disabled:opacity-50"
                      >
                        <Flame className="w-4 h-4 text-orange-500" />
                        Dramatic
                      </button>
                    </div>
                    
                    {/* Hook Variations */}
                    <div className="space-y-2">
                      <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Hook Styles</p>
                      <button
                        onClick={() => handleRegenerateStory('question_hook')}
                        disabled={regenerateLoading}
                        className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-sm text-left flex items-center gap-2 disabled:opacity-50"
                      >
                        <MessageCircle className="w-4 h-4 text-teal-500" />
                        Question Hook
                      </button>
                      <button
                        onClick={() => handleRegenerateStory('statistic_hook')}
                        disabled={regenerateLoading}
                        className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-sm text-left flex items-center gap-2 disabled:opacity-50"
                      >
                        <Hash className="w-4 h-4 text-cyan-500" />
                        Statistic Hook
                      </button>
                      <button
                        onClick={() => handleRegenerateStory('story_hook')}
                        disabled={regenerateLoading}
                        className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-sm text-left flex items-center gap-2 disabled:opacity-50"
                      >
                        <BookOpen className="w-4 h-4 text-emerald-500" />
                        Story Hook
                      </button>
                      <button
                        onClick={() => handleRegenerateStory('contrast_hook')}
                        disabled={regenerateLoading}
                        className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-sm text-left flex items-center gap-2 disabled:opacity-50"
                      >
                        <Shuffle className="w-4 h-4 text-violet-500" />
                        Contrast Hook
                      </button>
                    </div>
                    
                    {/* Smart Options */}
                    <div className="space-y-2">
                      <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Smart Options</p>
                      <button
                        onClick={() => handleRegenerateStory('more_viral')}
                        disabled={regenerateLoading}
                        className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-sm text-left flex items-center gap-2 disabled:opacity-50"
                      >
                        <TrendingUp className="w-4 h-4 text-red-500" />
                        Make More Viral
                      </button>
                      <button
                        onClick={() => handleRegenerateStory('add_facts')}
                        disabled={regenerateLoading}
                        className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-sm text-left flex items-center gap-2 disabled:opacity-50"
                      >
                        <BarChart3 className="w-4 h-4 text-blue-500" />
                        Add More Facts
                      </button>
                      <button
                        onClick={() => handleRegenerateStory('simplify')}
                        disabled={regenerateLoading}
                        className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-sm text-left flex items-center gap-2 disabled:opacity-50"
                      >
                        <Lightbulb className="w-4 h-4 text-yellow-500" />
                        Simplify Language
                      </button>
                      <button
                        onClick={() => handleRegenerateStory('add_controversy')}
                        disabled={regenerateLoading}
                        className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-sm text-left flex items-center gap-2 disabled:opacity-50"
                      >
                        <Flame className="w-4 h-4 text-orange-500" />
                        Add Controversy
                      </button>
                      <button
                        onClick={() => handleRegenerateStory('cinematic')}
                        disabled={regenerateLoading}
                        className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-sm text-left flex items-center gap-2 disabled:opacity-50"
                      >
                        <Film className="w-4 h-4 text-purple-500" />
                        Cinematic Style
                      </button>
                    </div>
                  </div>
                  
                  {/* Combined Options Section */}
                  <div className="mt-4 p-4 bg-white rounded-lg border border-indigo-200">
                    <h5 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                      <Layers className="w-4 h-4" />
                      Combine Multiple Options (Select any combination)
                    </h5>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      {/* AI Style Selection */}
                      <div>
                        <p className="text-xs font-semibold text-gray-600 mb-2">AI Style</p>
                        <select
                          value={selectedCombinedOptions.aiStyle || ''}
                          onChange={(e) => setSelectedCombinedOptions({
                            ...selectedCombinedOptions,
                            aiStyle: e.target.value as any || undefined
                          })}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                        >
                          <option value="">None</option>
                          <option value="chatgpt">ChatGPT</option>
                          <option value="claude">Claude</option>
                          <option value="google">Google AI</option>
                        </select>
                      </div>
                      
                      {/* Emotional Tone Selection */}
                      <div>
                        <p className="text-xs font-semibold text-gray-600 mb-2">Emotional Tone</p>
                        <select
                          value={selectedCombinedOptions.emotionalTone || ''}
                          onChange={(e) => setSelectedCombinedOptions({
                            ...selectedCombinedOptions,
                            emotionalTone: e.target.value as any || undefined
                          })}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                        >
                          <option value="">None</option>
                          <option value="awe">Awe & Wonder</option>
                          <option value="suspense">Suspenseful</option>
                          <option value="inspirational">Inspirational</option>
                          <option value="educational">Educational</option>
                          <option value="dramatic">Dramatic</option>
                        </select>
                      </div>
                      
                      {/* Hook Type Selection */}
                      <div>
                        <p className="text-xs font-semibold text-gray-600 mb-2">Hook Type</p>
                        <select
                          value={selectedCombinedOptions.hookType || ''}
                          onChange={(e) => setSelectedCombinedOptions({
                            ...selectedCombinedOptions,
                            hookType: e.target.value as any || undefined
                          })}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                        >
                          <option value="">None</option>
                          <option value="question">Question Hook</option>
                          <option value="statistic">Statistic Hook</option>
                          <option value="story">Story Hook</option>
                          <option value="contrast">Contrast Hook</option>
                        </select>
                      </div>
                      
                      {/* Smart Option Selection */}
                      <div>
                        <p className="text-xs font-semibold text-gray-600 mb-2">Smart Option</p>
                        <select
                          value={selectedCombinedOptions.smartOption || ''}
                          onChange={(e) => setSelectedCombinedOptions({
                            ...selectedCombinedOptions,
                            smartOption: e.target.value as any || undefined
                          })}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                        >
                          <option value="">None</option>
                          <option value="more_viral">Make More Viral</option>
                          <option value="add_facts">Add More Facts</option>
                          <option value="simplify">Simplify Language</option>
                          <option value="add_controversy">Add Controversy</option>
                          <option value="cinematic">Cinematic Style</option>
                        </select>
                      </div>
                    </div>
                    
                    {/* Selected Options Display */}
                    {Object.keys(selectedCombinedOptions).length > 0 && (
                      <div className="mt-3 p-2 bg-indigo-50 rounded-lg">
                        <p className="text-xs text-indigo-700">
                          Selected: {Object.entries(selectedCombinedOptions)
                            .filter(([_, value]) => value)
                            .map(([key, value]) => `${key}: ${value}`)
                            .join(', ') || 'None'}
                        </p>
                      </div>
                    )}
                    
                    <button
                      onClick={handleCombinedRegenerate}
                      disabled={regenerateLoading || Object.keys(selectedCombinedOptions).filter(k => selectedCombinedOptions[k as keyof typeof selectedCombinedOptions]).length === 0}
                      className="mt-3 w-full px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 justify-center"
                    >
                      <Wand2 className="w-4 h-4" />
                      Apply Combined Options
                    </button>
                  </div>
                  </>
                )}
                
                {/* Custom Instructions */}
                {showRegenerateMenu && (
                  <div className="mt-4 pt-4 border-t border-indigo-200">
                    {!showCustomInput ? (
                      <button
                        onClick={() => setShowCustomInput(true)}
                        disabled={regenerateLoading}
                        className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        <Settings2 className="w-4 h-4" />
                        Custom Instructions
                      </button>
                    ) : (
                      <div className="space-y-3">
                        <textarea
                          value={customInstructions}
                          onChange={(e) => setCustomInstructions(e.target.value)}
                          placeholder="Enter custom instructions for regeneration (e.g., 'Make it more emotional with focus on human impact' or 'Start with this exact hook: ...')"
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                          rows={3}
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleRegenerateStory('custom_smart', customInstructions)}
                            disabled={regenerateLoading || !customInstructions.trim()}
                            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                          >
                            <Wand2 className="w-4 h-4" />
                            Apply Custom Instructions
                          </button>
                          <button
                            onClick={() => {
                              setShowCustomInput(false);
                              setCustomInstructions('');
                            }}
                            disabled={regenerateLoading}
                            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                {showRegenerateMenu && (
                  <button
                    onClick={() => {
                      setShowRegenerateMenu(false);
                      setShowCustomInput(false);
                      setCustomInstructions('');
                    }}
                    className="mt-2 text-sm text-gray-500 hover:text-gray-700"
                  >
                    Hide Options
                  </button>
                )}
              </div>
            </div>
          </div>
          
          {/* Story Structure Breakdown */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <PenTool className="w-5 h-5" />
              Story Structure Breakdown
            </h3>
            
            <div className="space-y-4">
              {storyStructureGuide.map((part, idx) => {
                // Map the display labels to the actual keys in the story parts
                const partKeyMap: Record<string, keyof typeof finalStory.parts> = {
                  'Bold Hook': 'boldHook',
                  'Relatable Analogy': 'relatableAnalogy',
                  'Step-by-Step Reveal': 'stepByStepReveal',
                  'Contrast & Drama': 'contrastAndDrama',
                  'Emotional Tie-In': 'emotionalTieIn',
                  'Mystery Ending': 'mysteryEnding',
                  'Soft CTA': 'softCTA'
                };
                const content = finalStory.parts[partKeyMap[part.label]] || '';
                
                return (
                  <div key={idx} className="border-2 border-gray-200 rounded-lg overflow-hidden">
                    <div className={`p-4 bg-gradient-to-r ${part.color} text-white`}>
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          {part.icon}
                          <span className="font-semibold">{part.label}</span>
                        </div>
                        <button
                          onClick={() => copyToClipboard(content, `part-${idx}`)}
                          className={`px-3 py-1 rounded text-sm transition-colors ${
                            copied === `part-${idx}`
                              ? 'bg-white/30'
                              : 'bg-white/20 hover:bg-white/30'
                          }`}
                        >
                          {copied === `part-${idx}` ? '✓ Copied' : 'Copy'}
                        </button>
                      </div>
                      <p className="text-sm opacity-90 mt-1">{part.tips}</p>
                      <p className="text-xs opacity-75 mt-1 italic">Example: {part.example}</p>
                    </div>
                    <div className="p-4 bg-gray-50">
                      {content ? (
                        <p className="text-gray-800 whitespace-pre-wrap">{content}</p>
                      ) : (
                        <p className="text-gray-400 italic">No content generated yet</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
      
      {/* How It Works Guide */}
      <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl p-6 border-2 border-blue-200">
        <h3 className="text-lg font-semibold mb-4 text-blue-900 flex items-center gap-2">
          <Bot className="w-5 h-5" />
          How This Generator Works (Based on Your Winning Formula)
        </h3>
        <div className="space-y-3 text-sm text-gray-700">
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">
              1
            </div>
            <div>
              <p className="font-semibold text-gray-900">🟨 Input Your Topic</p>
              <p>Enter any topic - vague or specific. Something you're curious about.</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">
              2
            </div>
            <div>
              <p className="font-semibold text-gray-900">🟢 Get 10 Unusual Reasons</p>
              <p>AI finds surprising angles most people don't talk about.</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">
              3
            </div>
            <div>
              <p className="font-semibold text-gray-900">🟢 Zoom Into One Reason</p>
              <p>Select the most interesting angle to get 5 specific, emotional story ideas.</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">
              4
            </div>
            <div>
              <p className="font-semibold text-gray-900">🟢 Create Best Story</p>
              <p>AI generates your viral story following the exact 7-part structure.</p>
            </div>
          </div>
        </div>
        
        <div className="mt-4 p-3 bg-blue-100 rounded-lg">
          <p className="text-xs text-blue-800">
            <strong>Pro Tip:</strong> Your best performing stories combine unexpected facts with emotional resonance. 
            The brain storage story worked because it made abstract science deeply personal.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ViralStoryGenerator;